import SDKConnector from '../SDKConnector';
import { RAW_REQUEST, SUCCESS_MESSAGE, SUCCESS_STATUS_CODE, TRANSCRIPT, USER, XML_TYPE, BW_XSI_ENDPOINT_VERSION, WEBEX_CALLING_CONNECTOR_FILE, METHOD_START_MESSAGE, } from '../common/constants';
import { serviceErrorCodeHandler, getXsiActionEndpoint, getSortedVoicemailList, resolveContact, storeVoicemailList, fetchVoicemailList, uploadLogs, } from '../common/Utils';
import { CALLING_BACKEND, HTTP_METHODS, SORT, } from '../common/types';
import log from '../Logger';
import { JSON_FORMAT, MARK_AS_READ, MARK_AS_UNREAD, MESSAGE_MEDIA_CONTENT, TRANSCRIPT_CONTENT, VOICE_MESSAGING_MESSAGES, NO_VOICEMAIL_MSG, NO_VOICEMAIL_STATUS_CODE, RADIX_RAND, PREFIX, TRANSCRIPT_STATUS, MESSAGE_SUMMARY, CALLS, SUMMARY, NEW_MESSAGES, NEW_URGENT_MESSAGES, OLD_URGENT_MESSAGES, OLD_MESSAGES, METHODS, } from './constants';
export class WxCallBackendConnector {
    xsiEndpoint;
    userId;
    context;
    sdkConnector;
    xsiVoiceMessageURI;
    webex;
    authHeaders = null;
    constructor(webex, logger) {
        this.sdkConnector = SDKConnector;
        if (!this.sdkConnector.getWebex()) {
            SDKConnector.setWebex(webex);
        }
        this.context = Math.random().toString(RADIX_RAND).substring(PREFIX);
        this.webex = this.sdkConnector.getWebex();
        this.userId = this.webex.internal.device.userId;
        log.setLogger(logger.level, WEBEX_CALLING_CONNECTOR_FILE);
    }
    async init() {
        this.authHeaders = await this.getAuthHeaders();
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.INIT,
        };
        log.info(METHOD_START_MESSAGE, loggerContext);
        const response = this.setXsiVoiceMessageURI();
        return response;
    }
    getSDKConnector() {
        return this.sdkConnector;
    }
    async setXsiVoiceMessageURI() {
        let responseDetails;
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.SET_XSI_VOICE_MESSAGE_URI,
        };
        log.info(METHOD_START_MESSAGE, loggerContext);
        this.xsiEndpoint = await getXsiActionEndpoint(this.webex, loggerContext, CALLING_BACKEND.WXC);
        log.log(`XsiEndpoint is ${this.xsiEndpoint}`, loggerContext);
        if (this.userId) {
            this.xsiVoiceMessageURI = `${this.xsiEndpoint}/${BW_XSI_ENDPOINT_VERSION}/${USER}/${this.userId}/${VOICE_MESSAGING_MESSAGES}`;
            responseDetails = {
                statusCode: SUCCESS_STATUS_CODE,
                data: {},
                message: SUCCESS_MESSAGE,
            };
        }
        return responseDetails;
    }
    async getVoicemailList(offset, offsetLimit, sort, refresh) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.GET_VOICEMAIL_LIST,
        };
        log.info(`${METHOD_START_MESSAGE} with Offset: ${offset} Offset limit: ${offsetLimit} Sort type:${sort}`, loggerContext);
        let messageinfo;
        if (refresh) {
            const urlXsi = `${this.xsiVoiceMessageURI}${JSON_FORMAT}`;
            const sortParam = Object.values(SORT).includes(sort) ? sort : SORT.DEFAULT;
            try {
                const response = await this.webex.request({
                    uri: `${urlXsi}`,
                    method: HTTP_METHODS.GET,
                    headers: { ...this.authHeaders },
                });
                log.log(`Response code: ${response.statusCode}`, loggerContext);
                log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
                const voicemailListResponse = response.body;
                if (Object.keys(voicemailListResponse?.VoiceMessagingMessages?.messageInfoList).length === 0) {
                    messageinfo = [];
                }
                else if (!Array.isArray(voicemailListResponse?.VoiceMessagingMessages?.messageInfoList?.messageInfo)) {
                    messageinfo = Array(voicemailListResponse?.VoiceMessagingMessages?.messageInfoList?.messageInfo);
                }
                else {
                    messageinfo = voicemailListResponse?.VoiceMessagingMessages?.messageInfoList
                        ?.messageInfo;
                    messageinfo = getSortedVoicemailList(messageinfo, sortParam);
                }
                storeVoicemailList(this.context, messageinfo);
            }
            catch (err) {
                log.error(`Failed to get voicemail list: ${JSON.stringify(err)}`, loggerContext);
                await uploadLogs();
                const errorStatus = serviceErrorCodeHandler(err, loggerContext);
                return errorStatus;
            }
        }
        const { messages, moreVMAvailable } = fetchVoicemailList(this.context, offset, offsetLimit, loggerContext);
        const responseDetails = {
            statusCode: moreVMAvailable ? SUCCESS_STATUS_CODE : NO_VOICEMAIL_STATUS_CODE,
            data: {
                voicemailList: messages,
            },
            message: moreVMAvailable ? SUCCESS_MESSAGE : NO_VOICEMAIL_MSG,
        };
        log.log('Successfully fetched voicemail list', loggerContext);
        return responseDetails;
    }
    async getVoicemailContent(messageId) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.GET_VOICEMAIL_CONTENT,
        };
        log.info(`${METHOD_START_MESSAGE} with messageId: ${messageId}`, loggerContext);
        try {
            const voicemailContentUrl = `${this.xsiEndpoint}${messageId}`;
            const response = await this.webex.request({
                uri: `${voicemailContentUrl}`,
                method: HTTP_METHODS.GET,
                headers: { ...this.authHeaders },
            });
            const parser = new DOMParser();
            const xmlDOM = parser.parseFromString(response[RAW_REQUEST].response, XML_TYPE);
            const mediaDetails = xmlDOM.getElementsByTagName(MESSAGE_MEDIA_CONTENT)[0];
            const mediaType = mediaDetails.childNodes[1]?.textContent;
            const mediaContent = mediaDetails.childNodes[2]?.textContent;
            log.info(`Media type is  ${mediaType}`, loggerContext);
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {
                    voicemailContent: {
                        type: mediaType,
                        content: mediaContent,
                    },
                },
                message: SUCCESS_MESSAGE,
            };
            log.log('Successfully fetched voicemail content', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get voicemail content: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    async getVoicemailSummary() {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.GET_VOICEMAIL_SUMMARY,
        };
        log.info(METHOD_START_MESSAGE, loggerContext);
        try {
            const voicemailSummaryUrl = `${this.xsiEndpoint}/${BW_XSI_ENDPOINT_VERSION}/${USER}/${this.userId}/${CALLS}/${MESSAGE_SUMMARY}`;
            const response = await this.webex.request({
                uri: `${voicemailSummaryUrl}`,
                method: HTTP_METHODS.GET,
                headers: { ...this.authHeaders },
            });
            const parser = new DOMParser();
            const xmlDOM = parser.parseFromString(response[RAW_REQUEST].response, XML_TYPE);
            const voicemailSummary = xmlDOM.getElementsByTagName(SUMMARY)[0];
            const newMessages = voicemailSummary.getElementsByTagName(NEW_MESSAGES)[0];
            const newUrgentMessages = voicemailSummary.getElementsByTagName(NEW_URGENT_MESSAGES)[0];
            const oldMessages = voicemailSummary.getElementsByTagName(OLD_MESSAGES)[0];
            const oldUrgentMessages = voicemailSummary.getElementsByTagName(OLD_URGENT_MESSAGES)[0];
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {
                    voicemailSummary: {
                        newMessages: newMessages ? Number(newMessages.textContent) : 0,
                        newUrgentMessages: newUrgentMessages ? Number(newUrgentMessages.textContent) : 0,
                        oldMessages: oldMessages ? Number(oldMessages.textContent) : 0,
                        oldUrgentMessages: oldUrgentMessages ? Number(oldUrgentMessages.textContent) : 0,
                    },
                },
                message: SUCCESS_MESSAGE,
            };
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            log.log('Successfully fetched voicemail summary', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get voicemail summary: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    async voicemailMarkAsRead(messageId) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.VOICEMAIL_MARK_AS_READ,
        };
        log.info(`${METHOD_START_MESSAGE} with messageId: ${messageId}`, loggerContext);
        try {
            const voicemailContentUrl = `${this.xsiEndpoint}${messageId}/${MARK_AS_READ}`;
            const response = await this.webex.request({
                uri: voicemailContentUrl,
                method: HTTP_METHODS.PUT,
                headers: { ...this.authHeaders },
            });
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {},
                message: SUCCESS_MESSAGE,
            };
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            log.log('Successfully marked voicemail as read', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to mark voicemail as read: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    async voicemailMarkAsUnread(messageId) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.VOICEMAIL_MARK_AS_UNREAD,
        };
        log.info(`${METHOD_START_MESSAGE} with messageId: ${messageId}`, loggerContext);
        try {
            const voicemailContentUrl = `${this.xsiEndpoint}${messageId}/${MARK_AS_UNREAD}`;
            const response = await this.webex.request({
                uri: voicemailContentUrl,
                method: HTTP_METHODS.PUT,
                headers: { ...this.authHeaders },
            });
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {},
                message: SUCCESS_MESSAGE,
            };
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            log.log('Successfully marked voicemail as unread', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to mark voicemail as unread: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    async deleteVoicemail(messageId) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.DELETE_VOICEMAIL,
        };
        log.info(`${METHOD_START_MESSAGE} with messageId: ${messageId}`, loggerContext);
        try {
            const voicemailContentUrl = `${this.xsiEndpoint}${messageId}`;
            const response = await this.webex.request({
                uri: voicemailContentUrl,
                method: HTTP_METHODS.DELETE,
                headers: { ...this.authHeaders },
            });
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {},
                message: SUCCESS_MESSAGE,
            };
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            log.log('Successfully deleted voicemail', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to delete voicemail: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    async getVMTranscript(messageId) {
        const loggerContext = {
            file: WEBEX_CALLING_CONNECTOR_FILE,
            method: METHODS.GET_VM_TRANSCRIPT,
        };
        log.info(`${METHOD_START_MESSAGE} with messageId: ${messageId}`, loggerContext);
        try {
            const voicemailContentUrl = `${this.xsiEndpoint}${messageId}/${TRANSCRIPT}`;
            const response = await this.webex.request({
                uri: voicemailContentUrl,
                method: HTTP_METHODS.GET,
                headers: { ...this.authHeaders },
            });
            const parser = new DOMParser();
            const xmlDOM = parser.parseFromString(response[RAW_REQUEST].response, XML_TYPE);
            const status = xmlDOM.getElementsByTagName(TRANSCRIPT_STATUS)[0];
            const transcript = xmlDOM.getElementsByTagName(TRANSCRIPT_CONTENT)[0];
            const responseDetails = {
                statusCode: Number(response.statusCode),
                data: {
                    voicemailTranscript: transcript?.textContent,
                },
                message: status.textContent,
            };
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            log.log('Successfully fetched voicemail transcript', loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get voicemail transcript: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorStatus = serviceErrorCodeHandler(err, loggerContext);
            return errorStatus;
        }
    }
    resolveContact(callingPartyInfo) {
        return resolveContact(callingPartyInfo);
    }
    async getAuthHeaders() {
        const headers = {};
        if (this.webex?.config?.fedramp) {
            headers.Authorization = await this.webex.credentials.getUserToken();
        }
        return headers;
    }
}
